import { useDispatch } from "react-redux";

import { QueryObserverResult, RefetchOptions } from "@tanstack/react-query";

import {
  EditIcon,
  EyeCloseIcon,
  EyeOpenIcon,
  RetryIcon,
  TableIcon,
  TrashIcon
} from "@/assets/icons";
import TooltipButton from "@/components/ui/TooltipButton";
import { useAppFeatures } from "@/hooks/useAppFeatures";
import {
  setDatasetIsFetching,
  setSelectedDataset
} from "@/store/slice/datasetSlice";
import {
  toggleDataWeightSettingsPopup,
  toggleSpatialAnalysisResultTabularModal
} from "@/store/slice/uiSlice";
import { GetAnalyticDetailResponse } from "@/types/analysis";
import { Dataset } from "@/types/dataset";

import { twMerge } from "tailwind-merge";

interface DatasetItemMenuProps {
  handleOpenDatasetTabularModal: (
    e: React.MouseEvent<HTMLButtonElement>
  ) => void;
  onRemoveDataset: (e: React.MouseEvent<HTMLButtonElement>) => void;

  setShowEditPopup: (show: boolean) => void;
  refetch: (
    options?: RefetchOptions
  ) => Promise<QueryObserverResult<GetAnalyticDetailResponse, Error>>;
  onToggleDatasetVisibility: (e: React.MouseEvent<HTMLButtonElement>) => void;
  showEditPopup: boolean;
  isSpatialAnalysis: boolean;
  editButtonRef: React.RefObject<HTMLButtonElement>;
  isFocused: boolean;
  isError?: boolean;
  isComparsionMode?: boolean;
  data: Dataset;
}

export default function DatasetItemMenu(props: Readonly<DatasetItemMenuProps>) {
  const { isFilterAttributeAnalysisResultEnabled } = useAppFeatures();
  const {
    onToggleDatasetVisibility,
    handleOpenDatasetTabularModal,
    onRemoveDataset,
    setShowEditPopup,
    refetch,
    isSpatialAnalysis,
    isComparsionMode,
    showEditPopup,
    editButtonRef,
    isFocused,
    isError,
    data
  } = props;
  const dispatch = useDispatch();

  const isAvailable = data.data.available === true;

  const handleOpenSpatialAnalysisResultTabularModal = () => {
    dispatch(setSelectedDataset(data));
    dispatch(toggleSpatialAnalysisResultTabularModal());
  };

  const handleClickTableIcon = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!isAvailable || data.properties.isFetching || isError) return;
    return isSpatialAnalysis
      ? handleOpenSpatialAnalysisResultTabularModal()
      : handleOpenDatasetTabularModal(e);
  };

  const handleClickEditIcon = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isAvailable || data.properties.isFetching || isError) return;
    setShowEditPopup(true);
    dispatch(toggleDataWeightSettingsPopup(false));
  };

  const handleToggleVisibility = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!isAvailable || data.properties.isFetching || isError) return;
    onToggleDatasetVisibility(e);
  };

  const handleRefetch = () => {
    dispatch(
      setDatasetIsFetching({ datasetId: data.data.id, isFetching: false })
    );
    refetch();
  };

  const shouldShowEditAttribute =
    !isSpatialAnalysis ||
    (isSpatialAnalysis && isFilterAttributeAnalysisResultEnabled);

  return (
    <div className="flex items-center gap-1">
      {(isFocused || showEditPopup) && (
        <>
          <TooltipButton
            sideOffset={2}
            position="top-center"
            content="Show data table."
            dataTestId="table-button"
            onClick={handleClickTableIcon}
          >
            <TableIcon
              aria-label="Show data table"
              className={twMerge(
                isAvailable && !data.properties.isFetching && !isError
                  ? "fill-dim-gray"
                  : ["fill-light-gray", "cursor-not-allowed"]
              )}
            />
          </TooltipButton>
          {shouldShowEditAttribute && (
            <TooltipButton
              sideOffset={2}
              position="top-center"
              content="Edit attribute filter."
              dataTestId="edit-button"
              ref={editButtonRef}
              onClick={handleClickEditIcon}
              className={twMerge(showEditPopup && "bg-teal-50")}
            >
              <EditIcon
                className={twMerge(
                  showEditPopup ? "fill-teal-500" : "",
                  !isAvailable && [
                    "cursor-not-allowed",
                    "[&>path]:fill-light-gray"
                  ]
                )}
              />
            </TooltipButton>
          )}

          <TooltipButton
            sideOffset={2}
            position="top-center"
            content="Remove layer."
            dataTestId="trash-button"
            onClick={onRemoveDataset}
            className={twMerge(isSpatialAnalysis && "hidden")}
          >
            <TrashIcon className="fill-txtBody" />
          </TooltipButton>
        </>
      )}
      {isError && (
        <button
          data-testid="table-button"
          className="flex bg-transparent items-center justify-center h-[30px] px-1"
          onClick={handleRefetch}
        >
          <RetryIcon
            className={twMerge(
              isAvailable && !data.properties.isFetching
                ? "fill-dim-gray"
                : "fill-light-gray"
            )}
          />
        </button>
      )}
      {!isComparsionMode && (
        <TooltipButton
          sideOffset={2}
          position="top-center"
          content={data.properties?.isVisible ? "Hide layer." : "Show layer."}
          ariaLabel={`Toggle dataset ${data.data.display_name}'s visibility`}
          dataTestId="visibility-button"
          onClick={handleToggleVisibility}
        >
          {data.properties?.isVisible ? (
            <EyeOpenIcon
              className={twMerge(
                isAvailable && !data.properties.isFetching && !isError
                  ? "fill-dim-gray"
                  : ["fill-light-gray", "cursor-not-allowed"]
              )}
            />
          ) : (
            <EyeCloseIcon
              className={twMerge(
                isAvailable && !data.properties.isFetching && !isError
                  ? "fill-dim-gray"
                  : ["fill-light-gray", "cursor-not-allowed"]
              )}
            />
          )}
        </TooltipButton>
      )}
    </div>
  );
}
